/**
 *
 * "Maintenance summary form to add descriptions and comments"
 *
 * @file   SummaryForm.js
 * @author Lateral
 * @since  2023
 */

import {
  Box,
  Button,
  Checkbox,
  Grid,
  OutlinedInput,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Divider
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import RemoveRedEye from '@mui/icons-material/RemoveRedEye'
import { DeckMappToolTip } from 'components/tooltip/DeckMappToolTip'

import React, { useEffect, useState } from 'react'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import { useTheme } from '@mui/material/styles'
import { DatePicker } from '@mui/x-date-pickers'
import dayjs from 'dayjs'
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera'
import {
  crossbeamName,
  dateNextShutDownName,
  datePerformedName,
  deckRailsName,
  descriptionName,
  includeCrossbeams,
  includeDeckRails,
  includeRecommendations,
  photoKeysName,
  postServiceName,
  preServiceName,
  recommendationName,
  tonnageName
} from 'pages/common/maintenance/formNames'
import { CaptionPhotoForm } from './CaptionPhotoForm'
import { ONE_WEEK_MILLIS, useCurrentDeckContext } from 'components/currentDeckContext/CurrentDeckContext'
import { DeckMappDialog } from 'components/DeckMappDialog'
import Legend from '../mobile/components/Legend'
import { useSnackbar } from 'notistack'

const defaultString = ''

/*
 *The main form for Maintenance. Contains all the text data gathered for Maintenance (DeckRails, Crossbeam, etc).
 */
function SummaryForm({ data, onChange, isMobile, hideTitle, fullHeight }) {
  /**
   * Generates a form to add to add descriptions and comments of Maintenance
   *
   * @function
   *
   * @param {object} deckRevision - Object of deckrevion details
   * @param {object} data - Deck maintenance data, with photos
   * @param {object[]} selected - Array of selected panel objects
   * @param {number} materialSelected - Selected sideliner material number
   * @param {} onBomClick - Action on clicking B.O.M. table
   * @param {} onChange - Action on changing selection
   * @param {boolean} isMobile - If the view is mobile sized screen
   * @param {boolean} inMaintenanceMode - If the context in Maintenance mode
   *
   * @returns {object} - A form to input Maintenance summary
   */
  const { nonArchivedHistories, maintenanceFrequency, deckId } = useCurrentDeckContext()
  const defaultValues = {
    [datePerformedName]: dayjs(data?.deckRevisionHistory?.DatePerformed || dayjs()),
    [tonnageName]: data?.deckRevisionHistory?.Tonnage ?? defaultString,
    [descriptionName]: data?.deckRevisionHistory?.Description ?? defaultString,
    [preServiceName]: data?.deckRevisionHistory?.PreServiceComments ?? defaultString,
    [postServiceName]: data?.deckRevisionHistory?.PostServiceComments ?? defaultString,
    [deckRailsName]: data?.deckRevisionHistory?.DeckRailsComments ?? defaultString,
    [includeDeckRails]: data?.deckRevisionHistory?.DeckRailsComments?.length > 0 ?? false,
    [crossbeamName]: data?.deckRevisionHistory?.CrossbeamComments ?? defaultString,
    [includeCrossbeams]: data?.deckRevisionHistory?.CrossbeamComments?.length > 0 ?? false,
    [recommendationName]: data?.deckRevisionHistory?.Recomendations ?? defaultString,
    [includeRecommendations]: data?.deckRevisionHistory?.Recomendations?.length > 0 ?? false,
    [photoKeysName]: data?.photos ?? [],
    midServicePhotos: data?.midServicePhotos ?? [],
    postServicePhotos: data?.midServicePhotos ?? [],
    [dateNextShutDownName]: data?.deckRevisionHistory?.NextShutDownDate
      ? dayjs.isDayjs(data?.deckRevisionHistory?.NextShutDownDate)
        ? data?.deckRevisionHistory?.NextShutDownDate
        : dayjs(data?.deckRevisionHistory?.NextShutDownDate)
      : dayjs(nonArchivedHistories.histories[0]?.DatePerformed || dayjs()).add(
          (maintenanceFrequency || 0) * ONE_WEEK_MILLIS,
          'milliseconds'
        )
  }
  const [timer, setTimer] = useState('')
  const [photoCaptionsModal, setPhotoCaptionsModal] = useState({
    type: '',
    fields: []
  })

  const {
    control,
    getValues,
    register,
    clearErrors,
    reset,
    formState: { errors },
    setValue
  } = useForm({ defaultValues })

  useEffect(() => {
    if (data?.deckRevisionHistory?.NextShutDownDate) {
      return
    }
    reset({
      'Next Shutdown Date': dayjs(nonArchivedHistories.histories[0]?.DatePerformed || dayjs()).add(
        (maintenanceFrequency || 0) * ONE_WEEK_MILLIS,
        'milliseconds'
      )
    })
  }, [deckId, maintenanceFrequency])

  useEffect(() => {
    reset({
      Tonnage: data?.deckRevisionHistory?.Tonnage,
      'Maintenance Description': data?.deckRevisionHistory?.Description,
      'Pre Service Comments': data?.deckRevisionHistory?.PreServiceComments,
      'Post Service Comments': data?.deckRevisionHistory?.PostServiceComments,
      'Deck Rails Comments': data?.deckRevisionHistory?.DeckRailsComments,
      'Crossbeam Comments': data?.deckRevisionHistory?.CrossbeamComments,
      Recommendations: data?.deckRevisionHistory?.Recommendations,
      IncludeCrossbeams: !!data?.deckRevisionHistory?.CrossbeamComments,
      IncludeDeckRails: !!data?.deckRevisionHistory?.DeckRailsComments,
      IncludeRecommendations: !!data?.deckRevisionHistory?.Recommendations,
      PhotoKeys: data?.photos ?? getValues()[photoKeysName],
      'Next Shutdown Date': getValues()[dateNextShutDownName],
      'Date Performed': getValues()[datePerformedName],
      midServicePhotos: data?.midServicePhotos ?? getValues()['midServicePhotos'],
      postServicePhotos: data?.postServicePhotos ?? getValues()['postServicePhotos']
    })
  }, [data?.deckRevisionHistory, data?.photos])

  const { fields, append, remove, replace, update } = useFieldArray({
    control,
    name: photoKeysName
  })

  const {
    fields: fieldsMidServicePhotos,
    append: appendMidServicePhotos,
    remove: removeMidServicePhotos,
    replace: replaceMidServicePhotos,
    update: updateMidServicePhotos
  } = useFieldArray({
    control,
    name: 'midServicePhotos'
  })

  const {
    fields: fieldsPostServicePhotos,
    append: appendPostServicePhotos,
    remove: removePostServicePhotos,
    replace: replacePostServicePhotos,
    update: updatePostServicePhotos
  } = useFieldArray({
    control,
    name: 'postServicePhotos'
  })

  //Action on clicking upload photo
  const onPhotoUpload = (e, type) => {
    const files = Array.from(e.target.files)
    for (const file of files) {
      if (file) {
        if (file.size > 12582912) {
          enqueueSnackbar({
            message: `"${file.name}" is too big!. Please make sure it is 12MB or less.`,
            autoHideDuration: 3000,
            variant: 'info'
          })
        } else if (
          !file.type.includes('image') &&
          !file.type.includes('img') &&
          !file.name.toLowerCase().match(/\.(jpg|jpeg|png|gif|bmp|webp|heic|heif|svg)$/)
        ) {
          enqueueSnackbar({
            message: `"${file.name}" is of the wrong type!. Please choose an image file.`,
            autoHideDuration: 3000,
            variant: 'info'
          })
        } else {
          const reader = new FileReader()
          reader.onload = function () {
            const img = new Image()
            img.onload = function () {
              let fn = append
              if (type === 'midService') {
                fn = appendMidServicePhotos
              } else if (type === 'postService') {
                fn = appendPostServicePhotos
              }
              fn({ file: file, caption: '', height: img.height, width: img.width })
            }
            img.src = reader.result
          }

          reader.readAsDataURL(file)
          e.target.value = ''
        }
      }
    }
    onFormChange()
    clearErrors()
  }

  const photoSections = {
    preService: {
      title: 'Pre-Service Pictures'
    },
    midService: {
      title: 'Mid-Service Pictures'
    },
    postService: {
      title: 'Post-Service Pictures'
    }
  }

  const theme = useTheme()
  const { enqueueSnackbar } = useSnackbar()

  function onFormChange() {
    clearTimeout(timer)
    const timeoutId = setTimeout(() => onTimeoutSubmit(), 1000)
    setTimer(timeoutId)
  }

  async function onTimeoutSubmit() {
    try {
      onChange(getValues())
    } catch {
      //ignored
    }
  }

  // Action on clicking 'View Images/Add Caption'
  function onUpdatePhotoCaptions(data) {
    const { fields: selectedFields, type } = photoCaptionsModal
    if (selectedFields.length === 1) {
      let fn = update
      let fields_ = fields
      if (type === 'midService') {
        fn = updateMidServicePhotos
        fields_ = fieldsMidServicePhotos
      } else if (type === 'postService') {
        fn = updatePostServicePhotos
        fields_ = fieldsPostServicePhotos
      }
      fn(
        fields_.findIndex(({ id }) => id === data.Files[0].data.id),
        data.Files[0].data
      )
    } else {
      let fn = replace
      if (type === 'midService') {
        fn = replaceMidServicePhotos
      } else if (type === 'postService') {
        fn = replacePostServicePhotos
      }
      fn(data.Files.map((f) => f.data))
    }
    onFormChange()

    setPhotoCaptionsModal({
      fields: [],
      type: ''
    })
  }

  // Calander to input date
  function dateInput(name) {
    return (
      <Grid container alignItems="center">
        <Grid item xs={12} md={3}>
          <Typography variant="h5" component="h5">
            {name}
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          <Controller
            control={control}
            name={name}
            render={({ field: { onChange, ...rest } }) => {
              return (
                <DatePicker
                  {...rest}
                  onChange={(e) => {
                    onChange(e)
                    onFormChange()
                  }}
                  format="DD/MM/YYYY"
                />
              )
            }}
          />
          {errors[name] && <p>This field is required</p>}
        </Grid>
      </Grid>
    )
  }

  //Small text input box
  function textInput(name, required = false) {
    return (
      <Grid container alignItems="center">
        <Grid item xs={12} md={3}>
          <Typography variant="h5" component="h5">
            {name}
            <DeckMappToolTip title="Enter the amount of material (tonnes), that was processed since last shutdown, if known" />
          </Typography>
        </Grid>
        <Grid item xs={12} md={9}>
          <OutlinedInput
            id={name}
            name={name}
            color="secondary"
            fullWidth
            sx={{ width: isMobile ? '100%' : '50%' }}
            onChange={onFormChange}
            {...register(name, {
              required: required,
              onChange: () => onFormChange()
            })}
          />
          {errors[name] && <p>This field is required</p>}
        </Grid>
      </Grid>
    )
  }

  //Larger text input area
  function textAreaInput(name, required = false, include = null) {
    return (
      <Grid container alignItems="center">
        <Grid item xs={12} md={12}>
          <Stack direction="row" alignItems="center" sx={include ? { marginLeft: '-10px' } : null}>
            {include ? (
              <Controller
                control={control}
                name={include}
                render={({ field: { onChange, ...props } }) => (
                  <Checkbox
                    {...props}
                    checked={props.value}
                    onChange={(e) => {
                      onChange(e)
                      onFormChange()
                    }}
                    sx={{ color: `${theme.palette.supporting.highlight}` }}
                  />
                )}
              />
            ) : null}
            <Typography variant="h5" component="h5" sx={{ marginBottom: '0.2em' }}>
              {name}
            </Typography>
          </Stack>
        </Grid>
        <Grid item xs={12} md={12}>
          <OutlinedInput
            id={name}
            name={name}
            color="secondary"
            fullWidth
            multiline
            rows={3}
            onChange={() => {
              onFormChange()
            }}
            sx={{ padding: '0' }}
            {...register(name, {
              required: required,
              onChange: (event) => {
                setValue(include, !!event.target.value)
                onFormChange()
              }
            })}
          />
          {errors[name] && <p>This field is required</p>}
        </Grid>
      </Grid>
    )
  }

  return (
    <Box sx={{ height: isMobile ? null : '100%', maxWidth: isMobile ? undefined : '100%' }}>
      {!hideTitle && (
        <Typography variant="h5" component="h5">
          Maintenance Summary
        </Typography>
      )}

      <Box
        elevation={6}
        sx={{
          backgroundImage: 'none',
          height: isMobile ? null : fullHeight ? '100%' : '95%',
          marginTop: '0.5em',
          overflow: fullHeight ? 'hidden' : 'scroll'
        }}>
        <Legend />
        <form>
          <Typography variant="h5" component="h5" sx={{ backgroundColor: 'primary.contrastText', padding: '1em' }}>
            Create Maintenance Job
            <DeckMappToolTip title="Add content into the text boxes to include in the final report. Text boxes with a checkbox will be included if they are ticked" />
          </Typography>
          <Stack spacing={2}>
            {/* reusing code! */}
            {/* Date input calander */}
            {dateInput(datePerformedName)}

            {dateInput(dateNextShutDownName)}
            {/* Tonnage input box */}
            {textInput(tonnageName, true)}
            {/* Maintenance Description */}
            {textAreaInput(descriptionName, true)}
            {/* Preservice comments */}
            {textAreaInput(preServiceName, true)}
            {/* Post service comments */}
            {textAreaInput(postServiceName, true)}
            {/* Deck rails comments */}
            {textAreaInput(deckRailsName, false, includeDeckRails)}
            {/* Crossbeam Comments */}
            {textAreaInput(crossbeamName, false, includeCrossbeams)}
            {/* Recommendation text input area */}
            {textAreaInput(recommendationName, false, includeRecommendations)}
            {/* Image upload button */}
            <Stack spacing={2}>
              <Typography variant="h5" component="h5">
                Upload Media
                <DeckMappToolTip title="Upload media to include in the final report. Note this feature is disabled when in offline mode" />
              </Typography>
              {/* Image upload button */}
              <Box
                sx={{
                  marginBottom: '1em !important'
                }}>
                {Object.keys(photoSections).map((key) => {
                  const section = photoSections[key]
                  const fields_ =
                    key === 'preService'
                      ? fields
                      : key === 'midService'
                      ? fieldsMidServicePhotos
                      : fieldsPostServicePhotos

                  return (
                    <Stack spacing={2} key={key}>
                      <Typography variant="h5" component="h5">
                        {section.title}
                        <DeckMappToolTip title="Upload media to include in the final report. Note this feature is disabled when in offline mode" />
                      </Typography>

                      <Button
                        variant="outlined"
                        component="label"
                        sx={{ border: `dashed 1px ${theme.palette.text.highlight}`, height: '6em' }}
                        startIcon={<PhotoCameraIcon />}>
                        Upload
                        <input hidden accept="image/*" type="file" multiple onChange={(e) => onPhotoUpload(e, key)} />
                      </Button>

                      <Grid container spacing={1} flexDirection="column">
                        {fields_.map((f, i) => {
                          return (
                            <Grid display="flex" flexDirection="row" key={f.id} item sx={{ alignItems: 'center' }}>
                              <Typography flex="1">{`${f.file.name}${f.caption ? ` - ${f.caption}` : ''}`}</Typography>
                              <Grid display="flex" item>
                                <Tooltip title="View/Add Caption">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setPhotoCaptionsModal({
                                        type: key,
                                        fields: [fields_[i]]
                                      })
                                    }}>
                                    <RemoveRedEye />
                                  </IconButton>
                                </Tooltip>
                                <IconButton
                                  color="error"
                                  size="small"
                                  onClick={() => {
                                    let fn = remove
                                    if (key === 'midService') {
                                      fn = removeMidServicePhotos
                                    } else if (key === 'postService') {
                                      fn = removePostServicePhotos
                                    }
                                    fn(i)
                                  }}>
                                  <DeleteIcon />
                                </IconButton>
                              </Grid>
                            </Grid>
                          )
                        })}
                      </Grid>
                      {fields_.length ? (
                        <Box display="flex" justifyContent="center" mb="1em">
                          <Button
                            variant="outlined"
                            color="secondary"
                            onClick={() =>
                              setPhotoCaptionsModal({
                                fields: fields_,
                                type: key
                              })
                            }>
                            View Images/Add Captions
                          </Button>
                        </Box>
                      ) : null}

                      <Divider />
                    </Stack>
                  )
                })}
              </Box>
            </Stack>
          </Stack>
        </form>
      </Box>

      <DeckMappDialog
        title="Add Caption to Media"
        open={photoCaptionsModal.fields.length > 0}
        Actions={
          <>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => setPhotoCaptionsModal({ fields: [], type: '' })}>
              Cancel
            </Button>
            <Button variant="contained" color="secondary" form={`form`} type="submit">
              Save Changes
            </Button>
          </>
        }>
        <CaptionPhotoForm data={photoCaptionsModal.fields} onSubmit={onUpdatePhotoCaptions} />
      </DeckMappDialog>
    </Box>
  )
}

export default SummaryForm
